import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Diets.css';

const API_URL = `${process.env.REACT_APP_API_URL || 'https://wellness.nextriking.com/api'}/diets`;

const emptyOption = () => ({ title: 'Opción principal', items: [''] });
const emptyMeal = (name = '', moment = '') => ({ name, moment, options: [emptyOption()] });
const initialDiet = () => ({
  title: '',
  objective: '',
  introduction: '',
  meals: [
    emptyMeal('Desayuno', 'Comida 1'),
    emptyMeal('Media mañana', 'Comida 2'),
    emptyMeal('Almuerzo', 'Comida 3'),
    emptyMeal('Merienda', 'Comida 4'),
    emptyMeal('Cena', 'Comida 5'),
  ],
  tips: [''],
});

const cloneDiet = (diet) => ({
  title: diet.title || '',
  objective: diet.objective || '',
  introduction: diet.introduction || '',
  meals: diet.meals?.map((meal) => ({
    name: meal.name || '',
    moment: meal.moment || '',
    options: meal.options?.map((option) => ({ title: option.title || '', items: option.items?.length ? option.items : [''] })) || [emptyOption()],
  })) || [emptyMeal()],
  tips: diet.tips?.length ? diet.tips : [''],
});

const Diets = () => {
  const navigate = useNavigate();
  const [diets, setDiets] = useState([]);
  const [diet, setDiet] = useState(initialDiet);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadDiets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      setDiets(Array.isArray(response.data.body) ? response.data.body : []);
    } catch (error) {
      setMessage('No se pudieron cargar las dietas. Verificá la conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDiets(); }, []);

  const updateDiet = (field, value) => setDiet((current) => ({ ...current, [field]: value }));
  const updateMeal = (mealIndex, field, value) => setDiet((current) => ({
    ...current,
    meals: current.meals.map((meal, index) => index === mealIndex ? { ...meal, [field]: value } : meal),
  }));
  const updateOption = (mealIndex, optionIndex, field, value) => setDiet((current) => ({
    ...current,
    meals: current.meals.map((meal, index) => index === mealIndex ? {
      ...meal,
      options: meal.options.map((option, optionPosition) => optionPosition === optionIndex ? { ...option, [field]: value } : option),
    } : meal),
  }));
  const updateItem = (mealIndex, optionIndex, itemIndex, value) => setDiet((current) => ({
    ...current,
    meals: current.meals.map((meal, index) => index === mealIndex ? {
      ...meal,
      options: meal.options.map((option, optionPosition) => optionPosition === optionIndex ? {
        ...option,
        items: option.items.map((item, itemPosition) => itemPosition === itemIndex ? value : item),
      } : option),
    } : meal),
  }));

  const addMeal = () => setDiet((current) => ({ ...current, meals: [...current.meals, emptyMeal('Nueva comida', `Comida ${current.meals.length + 1}`)] }));
  const removeMeal = (mealIndex) => setDiet((current) => ({ ...current, meals: current.meals.filter((_, index) => index !== mealIndex) }));
  const addOption = (mealIndex) => setDiet((current) => ({ ...current, meals: current.meals.map((meal, index) => index === mealIndex ? { ...meal, options: [...meal.options, emptyOption()] } : meal) }));
  const removeOption = (mealIndex, optionIndex) => setDiet((current) => ({ ...current, meals: current.meals.map((meal, index) => index === mealIndex ? { ...meal, options: meal.options.filter((_, position) => position !== optionIndex) } : meal) }));
  const addItem = (mealIndex, optionIndex) => setDiet((current) => ({ ...current, meals: current.meals.map((meal, index) => index === mealIndex ? { ...meal, options: meal.options.map((option, position) => position === optionIndex ? { ...option, items: [...option.items, ''] } : option) } : meal) }));
  const removeItem = (mealIndex, optionIndex, itemIndex) => setDiet((current) => ({ ...current, meals: current.meals.map((meal, index) => index === mealIndex ? { ...meal, options: meal.options.map((option, position) => position === optionIndex ? { ...option, items: option.items.filter((_, itemPosition) => itemPosition !== itemIndex) } : option) } : meal) }));

  const saveDiet = async (event) => {
    event.preventDefault();
    if (!diet.title.trim() || !diet.objective.trim() || !diet.meals.length) {
      setMessage('Completá el título, objetivo y al menos una comida.');
      return;
    }
    try {
      setSaving(true);
      const response = editingId ? await axios.put(`${API_URL}/${editingId}`, diet) : await axios.post(API_URL, diet);
      const slug = response.data.body?.slug;
      setMessage(editingId ? 'Dieta actualizada correctamente.' : 'Dieta creada correctamente.');
      setEditingId(null);
      setDiet(initialDiet());
      await loadDiets();
      if (slug && !editingId) window.setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 50);
    } catch (error) {
      setMessage(error.response?.data?.body || 'No se pudo guardar la dieta.');
    } finally {
      setSaving(false);
    }
  };

  const editDiet = (item) => {
    setEditingId(item.id);
    setDiet(cloneDiet(item));
    setMessage(`Editando: ${item.title}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteDiet = async (item) => {
    if (!window.confirm(`¿Eliminar la dieta "${item.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/${item.id}`);
      if (editingId === item.id) { setEditingId(null); setDiet(initialDiet()); }
      setMessage('Dieta eliminada.');
      await loadDiets();
    } catch (error) {
      setMessage('No se pudo eliminar la dieta.');
    }
  };

  const shareUrl = (slug) => `${window.location.origin}/dietas/ver/${slug}`;
  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(shareUrl(slug));
      setMessage('Enlace copiado al portapapeles.');
    } catch (error) {
      window.prompt('Copiá este enlace:', shareUrl(slug));
    }
  };

  return (
    <main className="diets-admin">
      <header className="diets-header">
        <div><span className="diets-eyebrow">NUTRICIÓN</span><h1>Planes de alimentación</h1><p>Creá una guía clara y compartila con cada alumno desde un enlace.</p></div>
        <button type="button" className="diets-back" onClick={() => navigate('/dashboard')}>Volver al panel</button>
      </header>
      <div className="diets-layout">
        <section className="diet-editor">
          <div className="section-heading"><div><span className="section-kicker">{editingId ? 'EDICIÓN' : 'NUEVO PLAN'}</span><h2>{editingId ? 'Actualizar dieta' : 'Diseñá una dieta'}</h2></div>{editingId && <button type="button" className="text-button" onClick={() => { setEditingId(null); setDiet(initialDiet()); }}>Cancelar edición</button>}</div>
          {message && <div className="diet-message" role="status">{message}</div>}
          <form onSubmit={saveDiet}>
            <div className="diet-fields"><label>Nombre del plan<input value={diet.title} onChange={(event) => updateDiet('title', event.target.value)} placeholder="Ej. Definición y energía" required /></label><label>Objetivo<input value={diet.objective} onChange={(event) => updateDiet('objective', event.target.value)} placeholder="Ej. Reducir grasa manteniendo masa muscular" required /></label></div>
            <label className="full-label">Introducción para el alumno<textarea value={diet.introduction} onChange={(event) => updateDiet('introduction', event.target.value)} placeholder="Indicaciones generales, hidratación o recordatorios." rows="3" /></label>
            <div className="meal-editor-title"><div><span className="section-kicker">ESTRUCTURA</span><h3>Comidas del día</h3></div><button type="button" className="outline-button" onClick={addMeal}>+ Agregar comida</button></div>
            {diet.meals.map((meal, mealIndex) => <article className="meal-editor" key={`meal-${mealIndex}`}><div className="meal-top"><span>{String(mealIndex + 1).padStart(2, '0')}</span><input value={meal.moment} onChange={(event) => updateMeal(mealIndex, 'moment', event.target.value)} placeholder="Comida 1" /><input value={meal.name} onChange={(event) => updateMeal(mealIndex, 'name', event.target.value)} placeholder="Desayuno" /><button type="button" className="remove-button" onClick={() => removeMeal(mealIndex)} aria-label="Quitar comida">Quitar</button></div>
              {meal.options.map((option, optionIndex) => <div className="option-editor" key={`option-${optionIndex}`}><div className="option-title"><input value={option.title} onChange={(event) => updateOption(mealIndex, optionIndex, 'title', event.target.value)} placeholder="Opción principal" /><button type="button" className="text-button danger" onClick={() => removeOption(mealIndex, optionIndex)}>Quitar opción</button></div>{option.items.map((item, itemIndex) => <div className="food-row" key={`item-${itemIndex}`}><input value={item} onChange={(event) => updateItem(mealIndex, optionIndex, itemIndex, event.target.value)} placeholder="Ej. 150 g de pollo" /><button type="button" onClick={() => removeItem(mealIndex, optionIndex, itemIndex)} aria-label="Quitar alimento">x</button></div>)}<button type="button" className="text-button" onClick={() => addItem(mealIndex, optionIndex)}>+ Agregar alimento</button></div>)}
              <button type="button" className="add-option" onClick={() => addOption(mealIndex)}>+ Agregar alternativa</button></article>)}
            <section className="tips-editor"><span className="section-kicker">RECOMENDACIONES</span><h3>Tips y acompañamientos</h3>{diet.tips.map((tip, index) => <div className="food-row" key={`tip-${index}`}><input value={tip} onChange={(event) => updateDiet('tips', diet.tips.map((current, position) => position === index ? event.target.value : current))} placeholder="Ej. Infusiones sin azúcar" /><button type="button" onClick={() => updateDiet('tips', diet.tips.filter((_, position) => position !== index))} aria-label="Quitar tip">x</button></div>)}<button type="button" className="text-button" onClick={() => updateDiet('tips', [...diet.tips, ''])}>+ Agregar tip</button></section>
            <button className="save-diet" type="submit" disabled={saving}>{saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Publicar dieta'}</button>
          </form>
        </section>
        <aside className="diet-library"><div className="section-heading"><div><span className="section-kicker">BIBLIOTECA</span><h2>Tus dietas</h2></div><span className="diet-count">{diets.length}</span></div>{loading ? <p className="library-empty">Cargando dietas...</p> : diets.length ? diets.map((item) => <article className="diet-library-card" key={item.id}><span className="diet-card-label">PLAN COMPARTIBLE</span><h3>{item.title}</h3><p>{item.objective}</p><a href={shareUrl(item.slug)} target="_blank" rel="noreferrer">Ver como alumno</a><div className="share-link"><input value={shareUrl(item.slug)} readOnly aria-label="Enlace para compartir" /><button type="button" onClick={() => copyLink(item.slug)}>Copiar</button></div><div className="library-actions"><button type="button" onClick={() => editDiet(item)}>Editar</button><button type="button" className="danger" onClick={() => deleteDiet(item)}>Eliminar</button></div></article>) : <p className="library-empty">Todavía no hay dietas publicadas. Creá la primera desde el formulario.</p>}</aside>
      </div>
    </main>
  );
};

export default Diets;
